import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {SchemaInfoFields, TransformationFieldInfo} from '../RunSchemaSpecConstants';

type SchemaFieldReference = Pick<SchemaField, 'apiId' | 'name'>;

// Field names can't start with a field API ID's prefix (the server rejects them), so a key that does is an API ID.
const SCHEMA_FIELD_API_ID_PREFIX = 'tsf_';

export function isSchemaFieldApiId(key: string): boolean {
  return key.startsWith(SCHEMA_FIELD_API_ID_PREFIX);
}

function hasKey(fields: SchemaInfoFields, key: string | null | undefined): key is string {
  return key != null && key !== '' && Object.hasOwn(fields, key);
}

/**
 * The key a schema info's `fields` use for `field`.
 *
 * The server keys a field by its name, unless its name is also on another of the schema info's schemas (a plate's
 * schema and its wells' schema can both have a field of the same name), where it keys it by its API ID. A field
 * keeps whichever key it already has, so saving can't move it to the other schema's field. A field that isn't in
 * `fields` yet is keyed by its API ID, which can't refer to another schema's field.
 */
export function getSchemaInfoFieldKey(fields: SchemaInfoFields, field: SchemaFieldReference): string {
  if (hasKey(fields, field.apiId)) {
    return field.apiId;
  }
  if (hasKey(fields, field.name)) {
    return field.name;
  }
  return field.apiId || field.name;
}

export function getTransformationFieldInfo(
  fields: SchemaInfoFields,
  field: SchemaFieldReference
): TransformationFieldInfo | undefined {
  const key = getSchemaInfoFieldKey(fields, field);
  return hasKey(fields, key) ? fields[key] : undefined;
}

/**
 * The field a key of a schema info's `fields` refers to, from the first of `schemas` that has it: the field with
 * that API ID, or else the field with that name.
 */
export function findSchemaFieldForKey<TSchema extends {allFields: Array<SchemaField>}>(
  key: string,
  schemas: ReadonlyArray<TSchema | null | undefined>
): {field: SchemaField; schema: TSchema} | null {
  const presentSchemas = schemas.filter((schema): schema is TSchema => schema != null);
  for (const matches of [
    (field: SchemaField) => field.apiId === key,
    (field: SchemaField) => field.name === key,
  ]) {
    for (const schema of presentSchemas) {
      const field = schema.allFields.find(matches);
      if (field) {
        return {field, schema};
      }
    }
  }
  return null;
}
